import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    useMediaQuery,
    Theme,
} from '@mui/material';
import { Order } from 'models/order';
import { useMutation } from '@apollo/client/react/hooks';
import { deleteDraftOrderTemplate } from 'apollo/templates/order';
import InfiniteScroll from 'react-infinite-scroll-component';
import DraftOrderListItem from './draft-order-item/draft-order-item.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import ConfirmationDialog from 'components/orders/shared/confirmation-dialog.component';
import { ApolloCache, Reference } from '@apollo/client/core';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type DraftOrderListProps = {
    draftOrders: Order[];
    onFetchMore: () => void;
    removeOrder: (orderId: string) => void;
};
const DraftOrderList: FC<DraftOrderListProps> = ({ draftOrders, onFetchMore, removeOrder }: DraftOrderListProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [open, setOpen] = useState(false);
    const [orderId, setOrderId] = useState('');
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

    const [deleteDraftOrder, { loading, error }] = useMutation(deleteDraftOrderTemplate, {
        fetchPolicy: 'no-cache',
        onError: (err) => {
            console.log(err, 'error');
        },
        onCompleted: () => {
            // Delete from the draft order list
            removeOrder(orderId);
        },
        update(cache: ApolloCache<Order[]>) {
            cache.modify({
                fields: {
                    draftOrders(existingDraftOrderRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingDraftOrderRefs instanceof Array) {
                            return [...existingDraftOrderRefs].filter((draftOrderRef: Reference | undefined) => {
                                return orderId !== readField('_id', draftOrderRef);
                            });
                        }
                        return [];
                    },
                },
            });
        },
    });

    const handleDeleteClick = (orderId: string) => {
        setOrderId(orderId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOk = async () => {
        await deleteDraftOrder({
            variables: { _id: orderId },
        });
        setOpen(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, draftOrders?.length || 0);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={draftOrders ? draftOrders.length : 0}
                    next={onFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table size={!isLargeScreen ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell width="1"></TableCell>
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell>{translate('common:PATIENT')}</TableCell>
                                <TableCell>{translate('orders:ACTION_CODES')}</TableCell>
                                <TableCell>{translate('common:DENTIST')}</TableCell>
                                <TableCell>{translate('common:LAB')}</TableCell>
                                <TableCell>{translate('common:CREATED')}</TableCell>
                                <TableCell>{translate('common:LAST_UPDATED')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Fragment>
                                {draftOrders.map((order) => (
                                    <DraftOrderListItem
                                        key={order._id}
                                        order={order}
                                        onDeleteClick={handleDeleteClick}
                                    />
                                ))}
                            </Fragment>
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            {error ? <AlertSnackbar message={error.message} type="error" /> : undefined}
            {loading ? <BackdropLoader isOpen={loading} /> : undefined}
            <ConfirmationDialog
                open={open}
                handleClose={handleClose}
                handleOk={handleOk}
                title={translate('common:CONFIRM_TITLE')}
                confirm={translate('common:CONFIRM_DELETE')}
                process={translate('common:DELETE')}
            />
        </Fragment>
    );
};

export default DraftOrderList;
